import {
  createContext,
  type CSSProperties,
  type ElementType,
  type ReactNode,
  useContext,
  useState,
} from "react";

import { useInView } from "@/hooks/useInView";
import { cn } from "@/lib/cn";

const REVEAL_TRIGGER = { once: true, rootMargin: "0px 0px -12% 0px" };

const RevealContext = createContext<boolean | null>(null);

interface RevealGroupProps {
  className?: string;
  children: ReactNode;
}

/** Общий триггер: по отдельности подзаголовок войдёт в кадр позже заголовка */
export const RevealGroup = ({ className, children }: RevealGroupProps) => {
  const [ref, inView] = useInView<HTMLDivElement>(REVEAL_TRIGGER);

  return (
    <div
      ref={ref}
      className={className}
    >
      <RevealContext.Provider value={inView}>{children}</RevealContext.Provider>
    </div>
  );
};

interface RevealTextProps {
  as?: ElementType;
  className?: string;
  delay?: number;
  children: ReactNode;
}

/** Две копии: размытая гаснет, обычная проявляется. Так едет только прозрачность */
export const RevealText = ({
  as: Tag = "span",
  className,
  delay = 0,
  children,
}: RevealTextProps) => {
  const group = useContext(RevealContext);
  const [ref, inView] = useInView<HTMLElement>(REVEAL_TRIGGER);
  const [isDone, setIsDone] = useState(false);
  const isActive = group ?? inView;

  // После проигрывания убираем и класс, и вторую копию: остаётся обычный текст
  return (
    <Tag
      ref={ref}
      className={cn(!isDone && "landing-text-reveal", className)}
      data-revealed={isActive && !isDone ? "" : undefined}
      style={{ "--reveal-delay": `${delay}ms` } as CSSProperties}
      onAnimationEnd={() => setIsDone(true)}
    >
      {isDone ? (
        children
      ) : (
        <>
          <span
            aria-hidden
            className="landing-text-reveal-glow"
          >
            {children}
          </span>
          <span className="landing-text-reveal-body">{children}</span>
        </>
      )}
    </Tag>
  );
};
